import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { defineMessages } from 'react-intl';

import type { getCardSpecialEditionBonus_anyCardInterface } from './__generated__/bonus.graphql.ts';
import { withFragments } from './gql/index.ts';

export const powerWithSkinBonus = (
  power: string,
  skinEdition: Nullable<{ bonus: Nullable<number> }>
): number => {
  const skinBonus = skinEdition?.bonus ? skinEdition.bonus - 1 : 0;
  return (Number(power) || 1) + skinBonus;
};

export const getCardSpecialEditionBonus = withFragments(
  (card: getCardSpecialEditionBonus_anyCardInterface) => {
    const bestBoard = card.eligibleBoardsWithSpecialEditionBonus.reduce<
      Nullable<
        getCardSpecialEditionBonus_anyCardInterface['eligibleBoardsWithSpecialEditionBonus'][number]
      >
    >(
      (best, candidate) =>
        !best || candidate.bonus.bonusBasisPoints > best.bonus.bonusBasisPoints
          ? candidate
          : best,
      null
    );

    if (!bestBoard?.bonus.bonusBasisPoints) {
      return null;
    }

    const { board, bonus } = bestBoard;
    const expiryDates = bonus.editions
      .map(({ until }) => until)
      .filter((until): until is Date => !!until);

    return {
      basisPoints: bonus.bonusBasisPoints,
      competition: board.title,
      until: expiryDates.length
        ? new Date(Math.max(...expiryDates.map(date => date.getTime())))
        : null,
    };
  },
  {
    anyCardInterface: gql`
      fragment getCardSpecialEditionBonus_anyCardInterface on AnyCardInterface {
        slug
        eligibleBoardsWithSpecialEditionBonus {
          board {
            id
            title
          }
          bonus {
            key
            bonusBasisPoints
            editions {
              until
            }
          }
        }
      }
    ` as TypedDocumentNode<getCardSpecialEditionBonus_anyCardInterface>,
  }
);

export const bonusMessages = defineMessages({
  captain: {
    defaultMessage: 'Captain bonus',
  },
  levelBonus: {
    defaultMessage: 'XP bonus',
  },
  scarcity: {
    defaultMessage: 'Scarcity bonus',
  },
  transferMalus: {
    defaultMessage: 'Non transferrable XP',
  },
  collection: {
    defaultMessage: 'Collection bonus',
  },
  season: {
    defaultMessage: 'Season bonus',
  },
  club: {
    defaultMessage: 'Club bonus',
  },
  nationality: {
    defaultMessage: 'Nationality bonus',
  },
  position: {
    defaultMessage: 'Position bonus',
  },
  specialEdition: {
    defaultMessage: 'Special Edition bonus',
  },
});
